import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import {
  SowingChartData,
  SowingCrops,
} from 'src/app/graph/chart-seminabilita/chart-seminabilita.component';
import {
  DailyWeatherData,
  SixHourWeatherData,
} from 'src/app/graph/section-sliderWeek/section-sliderWeek.component';
import moment from 'moment';
import { HistoricalRainfallData } from 'src/app/graph/chart-piovosita-storico/chart-piovosita-storico.component';
import { GatewayService } from 'src/assets/data/enum/gatewayService.enum';
import { environment } from '../../../environments/environment';
import { FarmWeatherLocation } from '../_models/farmWeatherLocation';
import { HeaderMeteoData } from '../../graph/header-meteo/header-meteo.component';
import { WindChartData } from '../../graph/chart-vento/chart-vento.component';
import { PrecipitationChartData } from '../../graph/chart-precipitazioni/chart-precipitazioni.component';
import { IrrorationChartData } from '../../graph/chart-irrorazione/chart-irrorazione.component';
import { AirTempChartData } from '../../graph';
import { AirTempPrecChartData } from '../../graph/chart-temp-prec/chart-temp-prec.component';
import { PrecipitationProbChartData } from '../../graph/chart-prob-prec/chart-prob-prec.component';

export enum WeatherDataAggregation {
  Hourly = 'hourly',
  Daily = 'daily',
}

@Injectable()
export class WeatherHubService {
  private readonly gatewayBaseUrl = environment.apiGatewayUrl;
  private readonly gatewayServiceWeatherHub = GatewayService.WEATHER_HUB;
  private readonly weatherHubUrl = this.gatewayBaseUrl + this.gatewayServiceWeatherHub;

  constructor(private readonly http: HttpClient) {}

  /**
   * Retrieves historical weather data for a specific location and date range.
   *
   * @param {Object} params - Parameters for the request
   * @param {number} [params.location_id] - The ID of the weather location
   * @param {number} [params.longitude] - Longitude of the location
   * @param {number} [params.latitude] - Latitude of the location
   * @param {string} params.start_date - Start date for historical data (YYYY-MM-DD)
   * @param {string} params.end_date - End date for historical data (YYYY-MM-DD)
   * @param {WeatherDataAggregation} params.aggregation - Aggregation level (daily/hourly). Default is daily
   */
  getHistoricalData(params: {
    location_id?: number;
    longitude?: number;
    latitude?: number;
    start_date: string | Date;
    end_date: string | Date;
    aggregation: WeatherDataAggregation;
  }): Observable<any> {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    return this.http.get(`${this.weatherHubUrl}/v1/weather/history`, { params: httpParams });
  }

  /**
   * Retrieves weather forecast data for a specific location.
   *
   * @param {number} location_id - The ID of the weather location
   * @param {WeatherDataAggregation} aggregation - Aggregation level (daily/hourly). Default is daily
   * @returns {Observable<any>} Observable emitting the forecast weather data
   */
  getForecastData(
    location_id: number,
    aggregation: WeatherDataAggregation = WeatherDataAggregation.Daily
  ): Observable<any> {
    const params = new HttpParams()
      .set('location_id', location_id.toString())
      .set('aggregation', aggregation);
    return this.http.get(`${this.weatherHubUrl}/v1/weather/forecast`, { params });
  }

  /**
   * Retrieves the current weather data for a specific location.
   *
   * @param {number} location_id - The ID of the weather location
   * @returns {Observable<any>} Observable emitting the current weather data
   */
  getCurrentData(location_id: number): Observable<any> {
    const params = new HttpParams().set('location_id', location_id.toString());
    return this.http.get(`${this.weatherHubUrl}/v1/weather/current`, { params });
  }

  /**
   * Retrieves agrometeorological data for a specific location.
   *
   * @param {number} location_id - The ID of the weather location
   * @returns {Observable<any>} Observable emitting the agrometeo data
   */
  getAgrometeoData(location_id: number): Observable<any> {
    const params = new HttpParams().set('location_id', location_id.toString());
    return this.http.get(`${this.weatherHubUrl}/v1/weather/agrometeo`, { params });
  }

  /**
   * Builds weather banner data from the raw weather API and selected location.
   *
   * @param {FarmWeatherLocation} location - The selected farm weather location
   * @param {any} weatherData - The raw weather data response
   * @returns {HeaderMeteoData | null} - Formatted header data for display, or null if input is invalid
   */
  buildMeteoHeaderData(location: FarmWeatherLocation, weatherData: any): HeaderMeteoData {
    if (!location || !weatherData) return null;
    const data = weatherData.data.current;
    const { units } = weatherData;

    const pictocodes = data?.pictocode_detailed ?? data?.pictocode;

    return {
      timestamp: data.time[0],
      city: `${location.city ?? location.city_alt}, ${location.country ?? ''}`,
      latitude: location.latitude,
      longitude: location.longitude,
      temperature_current: data.temperature[0],
      temperature_max: data.temperature_max?.[0],
      temperature_min: data.temperature_min?.[0],
      temperature_unit: units.temperature,
      precipitation: data.precipitation[0],
      precipitation_unit: units.precipitation,
      windspeed: data.windspeed[0],
      windspeed_unit: units.windspeed,
      weather_class: pictocodes?.[0]?.class,
      weather_icon: pictocodes?.[0]?.icon,
      weather_icon_s: pictocodes?.[0]?.['icon-s'],
      weather_icon_w: pictocodes?.[0]?.['icon-w'],
      weather_icon_g: pictocodes?.[0]?.['icon-g'],
      weather_video: pictocodes?.[0]?.video,
      weather_status: pictocodes?.[0]?.i18n,
    } as HeaderMeteoData;
  }

  /**
   * Prepares wind chart data from raw weather data.
   *
   * @param {any} weatherData - Raw weather API data.
   * @returns {WindChartData | null} Data formatted for wind chart.
   */
  buildWindChartData(weatherData: any): WindChartData {
    if (!weatherData) return null;
    return {
      timestamp: weatherData.time.map((v) => v),
      windSpeed: weatherData.windspeed.map((v) => v),
      direction: weatherData.winddirection.map((v) => v),
    } as WindChartData;
  }

  /**
   * Builds precipitation chart data including cumulative values.
   *
   * @param {any} weatherData - Raw weather data.
   * @returns {PrecipitationChartData | null} Data for precipitation charts.
   */
  buildPrecipitationChartData(weatherData: any): PrecipitationChartData {
    if (!weatherData) return null;

    const cumulative = [];
    weatherData.precipitation
      .map((p: number) => p)
      .reduce(
        (prev: number, curr: number, i: number) =>
          (cumulative[i] = Math.round((prev + curr) * 100) / 100.0),
        0
      );

    return {
      timestamp: weatherData.time.map((v) => v),
      precipitation: weatherData.precipitation.map((v) => v),
      cumulated: cumulative,
      weather: weatherData.pictocode?.map((json) => {
        return {
          icon: json.icon,
          value: json.code,
          key: json.i18n,
        } as WeatherData;
      }),
    };
  }

  /**
   * Builds spray/irrigation window chart data.
   *
   * @param {any} weatherData - Raw weather forecast data.
   * @returns {IrrorationChartData | null} Data for spray activity windows.
   */
  buildIrrorationChartData(weatherData: any): IrrorationChartData {
    if (!weatherData) return null;
    const data = weatherData.data.forecast_agrometeo.forecast_1h;
    const spraying_data = weatherData.data.forecast_spray_sowing;
    return {
      timestamp: spraying_data.time.map((v) => v),
      irroration: spraying_data.spraywindow.map((v) => v),
      weather: data.pictocode?.map((json) => json.class),
    };
  }

  /**
   * Builds air temperature chart data.
   *
   * @param {any} weatherData - Raw weather forecast.
   * @returns {AirTempChartData | null} Chart-ready air temperature data.
   */
  buildAirTemperatureChartData(weatherData: any): AirTempChartData {
    if (!weatherData) return null;
    return {
      timestamp: weatherData.time.map((v) => v),
      temperature: weatherData.temperature.map((v) => v),
      weather: weatherData?.pictocode?.map((json) => {
        return {
          icon: json.icon,
          value: json.code,
          key: json.i18n,
        } as WeatherData;
      }),
    };
  }

  /**
   * Combines temperature and precipitation data into one chart dataset.
   *
   * @param {any} weatherData - Raw weather data.
   * @returns {AirTempPrecChartData | null} Combined temp/precipitation chart data.
   */
  buildAirTemperaturePrecipitationChartData(weatherData: any): AirTempPrecChartData {
    if (!weatherData) return null;
    return {
      timestamp: weatherData.time.map((v) => v),
      temperature: weatherData.temperature.map((v) => v),
      precipitation: weatherData.precipitation.map((v) => v),
      weather:
        weatherData?.pictocode?.map((json) => {
          return {
            icon: json.icon,
            value: json.code,
            key: json.i18n,
          } as WeatherData;
        }) ?? [],
    };
  }

  /**
   * Builds precipitation and probability of precipitation chart data.
   *
   * @param {any} weatherData - Weather forecast data.
   * @returns {PrecipitationProbChartData | null} Chart-ready data.
   */
  buildPrecipitationProbabilityChartData(weatherData: any): PrecipitationProbChartData {
    if (!weatherData) return null;
    return {
      timestamp: weatherData.time.map((v) => v),
      precipitation: weatherData.precipitation.map((v) => v),
      probability: weatherData.precipitation_probability?.map((v) => v),
      weather: weatherData.pictocode?.map((json) => {
        return {
          icon: json.icon,
          value: json.code,
          key: json.i18n,
        } as WeatherData;
      }),
    };
  }

  /**
   * Builds sowing recommendation chart data.
   *
   * @param {any} weatherData - Forecast data.
   * @returns {SowingChartData | null} Crop-specific sowing data.
   */
  buildSowingChartData(weatherData: any): SowingChartData {
    if (!weatherData) return null;
    const data = weatherData.data.forecast_spray_sowing;
    const dataset = {} as SowingChartData;
    dataset.time = data.time.map((t) => t);
    for (const crop of SowingCrops) {
      dataset[crop] = data['sowing_' + crop].map((v) => v);
    }
    return dataset;
  }

  /**
   * Builds weekly slider data with daily and detailed 6-hour weather.
   *
   * @param {any} weatherData - Forecast data.
   * @returns {DailyWeatherData[] | null} Weekly weather slider data.
   */
  buildWeekSliderData(weatherData: any): DailyWeatherData[] {
    if (!weatherData) return null;
    const data_1d = weatherData?.data?.forecast_agrometeo?.forecast_1d;
    const data_6h = weatherData?.data?.forecast_agrometeo?.forecast_6h;
    const ranges = this.timeRanges();

    const sixHourData = data_6h.time
      .map((v) => v)
      .map((value: any, index: number) => {
        return {
          timestamp: value,
          partOfTheDay: data_6h.part_of_day[index],
          timeRange: ranges.find((range: string) => range.startsWith(moment(value).format('HH'))),
          temperature: data_6h.temperature[index],
          perceivedTemperature: data_6h.felttemperature[index],
          precipitations: data_6h.precipitation[index],
          precipitationProbability: data_6h.precipitation_probability[index],
          convectivePrecipitations: data_6h.convective_precipitation[index],
          snowPrecipitations: data_6h.snow_precipitation[index],
          windSpeed: data_6h.windspeed[index],
          relativeHumidity: data_6h.relativehumidity[index],
          pressureAtSeaLevel: data_6h.sealevelpressure[index],
        } as SixHourWeatherData;
      });

    return data_1d.time
      .map((v) => v)
      .map((value: any, index: number) => {
        return {
          timestamp: value,
          temperatureMax: data_1d.temperature_max[index],
          temperatureMin: data_1d.temperature_min[index],
          windSpeed: data_1d.windspeed[index],
          precipitation: data_1d.precipitation[index],
          details: sixHourData.filter(
            (sixhourdate) =>
              moment(value).format('YYYY-MM-DD') ===
              moment(sixhourdate.timestamp).format('YYYY-MM-DD')
          ),
          weather: {
            icon: data_1d.pictocode?.[index]?.class,
            value: data_1d.pictocode?.[index]?.code,
            key: data_1d.pictocode?.[index]?.i18n,
          } as WeatherData,
        } as DailyWeatherData;
      });
  }

  /**
   * Builds weekly slider from hourly forecast data.
   *
   * @param {any} weatherData - Hourly forecast.
   * @returns {DailyWeatherData[] | null} Weekly slider view.
   */
  buildWeekSliderFromForecast1h(weatherData: any): DailyWeatherData[] {
    if (!weatherData) return null;
    const sixHourData = this.aggregateHourlyTo6Hours(weatherData);
    const dailyData = this.aggregateHourlyToDaily(weatherData);

    dailyData.forEach((data, index) => {
      dailyData[index].details = sixHourData.filter(
        (sixhourdate) =>
          moment(data.timestamp).format('YYYY-MM-DD') ===
          moment(sixhourdate.timestamp).format('YYYY-MM-DD')
      );
    });

    return dailyData;
  }

  /**
   * Builds historical rainfall chart data.
   *
   * @param {any} weatherData - Historical weather data.
   * @returns {HistoricalRainfallData[] | null} Rainfall over time.
   */
  buildHistoryRainfallChartData(weatherData: any): HistoricalRainfallData[] {
    if (!weatherData) return null;
    return weatherData.time
      .map((v) => v)
      .map((time: string, index: number) => {
        return {
          time,
          precipitation: weatherData.precipitation[index],
        } as HistoricalRainfallData;
      });
  }

  /**
   * Calculates the total precipitation from weather data.
   *
   * @param {any} weatherData - Weather data containing precipitation array.
   * @returns {number} Total precipitation.
   */
  buildCumulatedRainData(weatherData: any): number {
    if (!weatherData) return null;
    return (weatherData.precipitation as number[]).reduce((acc, current) => acc + current, 0);
  }

  /**
   * Returns predefined time ranges used to group hourly weather data.
   *
   * @returns {string[]} Array of time range strings.
   */
  private timeRanges() {
    return ['00:00/06:00', '06:00/12:00', '12:00/18:00', '18:00/24:00'];
  }

  /**
   * Returns predefined parts of the day labels.
   *
   * @returns {string[]} Array of day part strings.
   */
  private partsOfTheDay() {
    return ['night', 'morning', 'afternoon', 'evening'];
  }

  /**
   * Aggregates hourly weather data into 6-hour intervals.
   *
   * @param {any} weatherData - Raw hourly weather data.
   * @returns {SixHourWeatherData[]} Aggregated weather data in 6-hour chunks.
   */
  private aggregateHourlyTo6Hours(weatherData: any): SixHourWeatherData[] {
    // Prepare empty arrays for aggregated output
    const aggregatedData = {
      timestamp: [],
      temperature: [],
      perceivedTemperature: [],
      precipitations: [],
      precipitationProbability: [],
      convectivePrecipitations: [],
      snowPrecipitations: [],
      windSpeed: [],
      relativeHumidity: [],
      pressureAtSeaLevel: [],
    };

    const interval = 6; // Define chunk size (6 hours)
    let currentIndex = 0;

    // Iterate through all time values in 6-hour steps
    while (currentIndex < weatherData.time?.length) {
      const endIndex = Math.min(currentIndex + interval, weatherData.time.length);

      // Extract slices for this 6-hour chunk
      const time = weatherData.time.slice(currentIndex, endIndex);
      const temperature = weatherData.temperature?.slice(currentIndex, endIndex);
      const felttemperature = weatherData.felttemperature?.slice(currentIndex, endIndex);
      const precipitation = weatherData.precipitation?.slice(currentIndex, endIndex);
      const probability = weatherData.precipitation_probability?.slice(currentIndex, endIndex);
      const convectivePrecipitations = weatherData.convectivePrecipitations?.slice(
        currentIndex,
        endIndex
      );
      const snowPrecipitations = weatherData.snowPrecipitations?.slice(currentIndex, endIndex);
      const windSpeed = weatherData.windspeed?.slice(currentIndex, endIndex);
      const relativehumidity = weatherData.relativehumidity?.slice(currentIndex, endIndex);
      const sealevelpressure = weatherData.sealevelpressure.slice(currentIndex, endIndex);

      // Compute and store aggregated values
      const avgTemperature = temperature?.length
        ? temperature.reduce((sum, value) => sum + value, 0) / temperature.length
        : 0;
      const avgFelttemperature = felttemperature?.length
        ? felttemperature.reduce((sum, value) => sum + value, 0) / felttemperature.length
        : 0;
      const avgWindSpeed = windSpeed?.length
        ? windSpeed.reduce((sum, value) => sum + value, 0) / windSpeed.length
        : 0;
      const avgRelativehumidity = relativehumidity?.length
        ? relativehumidity.reduce((sum, value) => sum + value, 0) / relativehumidity.length
        : 0;
      const avgSealevelpressure = sealevelpressure?.length
        ? sealevelpressure.reduce((sum, value) => sum + value, 0) / sealevelpressure.length
        : 0;

      const sumConvectivePrecipitations =
        convectivePrecipitations?.reduce((sum, value) => sum + value, 0) ?? 0;
      const sumSnowPrecipitations = snowPrecipitations?.reduce((sum, value) => sum + value, 0) ?? 0;
      const sumPrecipitation = precipitation?.reduce((sum, value) => sum + value, 0) ?? 0;

      const maxProbability = probability ? Math.max(...probability) : 0;

      aggregatedData.timestamp.push(time[0]);
      aggregatedData.precipitations.push(Math.round(sumPrecipitation * 100) / 100);
      aggregatedData.temperature.push(Math.round(avgTemperature * 100) / 100);
      aggregatedData.perceivedTemperature.push(Math.round(avgFelttemperature * 100) / 100);
      aggregatedData.precipitationProbability.push(maxProbability);
      aggregatedData.convectivePrecipitations.push(sumConvectivePrecipitations);
      aggregatedData.snowPrecipitations.push(sumSnowPrecipitations);
      aggregatedData.windSpeed.push(Math.round(avgWindSpeed * 100) / 100);
      aggregatedData.relativeHumidity.push(Math.round(avgRelativehumidity));
      aggregatedData.pressureAtSeaLevel.push(Math.round(avgSealevelpressure));

      currentIndex += interval;
    }

    // Map 6-hour chunks to human-readable parts of the day
    const partsOfTheDay = this.partsOfTheDay();
    const ranges = this.timeRanges();

    // Construct final array of SixHourWeatherData objects
    return aggregatedData.timestamp
      .map((v) => v)
      .map((value: any, index: number) => {
        return {
          timestamp: value,
          partOfTheDay: partsOfTheDay[index % partsOfTheDay.length],
          timeRange: ranges[index % ranges.length],
          temperature: aggregatedData.temperature[index],
          perceivedTemperature: aggregatedData.perceivedTemperature[index],
          precipitations: aggregatedData.precipitations[index],
          precipitationProbability: aggregatedData.precipitationProbability[index],
          convectivePrecipitations: aggregatedData.convectivePrecipitations[index],
          snowPrecipitations: aggregatedData.snowPrecipitations[index],
          windSpeed: aggregatedData.windSpeed[index],
          relativeHumidity: aggregatedData.relativeHumidity[index],
          pressureAtSeaLevel: aggregatedData.pressureAtSeaLevel[index],
        } as SixHourWeatherData;
      });
  }

  /**
   * Aggregates hourly weather data into daily summaries.
   *
   * @param {any} weatherData - Raw hourly weather forecast.
   * @returns {DailyWeatherData[]} Daily aggregated weather data.
   */
  private aggregateHourlyToDaily(weatherData: any): DailyWeatherData[] {
    const aggregatedData = {
      timestamp: [],
      temperatureMax: [],
      temperatureMin: [],
      windSpeed: [],
      precipitation: [],
      pictocode: [],
    };

    const interval = 24; // 24-hour interval for daily aggregation
    let currentIndex = 0;

    // Iterate through each 24-hour block
    while (currentIndex < weatherData.time?.length) {
      const pictocodeMap: Map<number, { weatherData: WeatherData; counter: number }> = new Map();

      const endIndex = Math.min(currentIndex + interval, weatherData.time.length);

      // Extract slices for this day
      const time = weatherData.time.slice(currentIndex, endIndex);
      const temperature = weatherData.temperature?.slice(currentIndex, endIndex);
      const precipitation = weatherData.precipitation?.slice(currentIndex, endIndex);
      const windSpeed = weatherData.windspeed?.slice(currentIndex, endIndex);
      const pictocodes = weatherData.pictocode?.slice(currentIndex, endIndex);

      // Calculate max and min temperature, average wind speed, and total precipitation
      const temperatureMax = Math.max(...temperature);
      const temperatureMin = Math.min(...temperature);
      const avgWindSpeed = windSpeed?.length
        ? windSpeed.reduce((sum, value) => sum + value, 0) / windSpeed.length
        : 0;
      const sumPrecipitation = precipitation?.reduce((sum, value) => sum + value, 0) ?? 0;

      // Count frequency of each pictocode (weather icon)
      pictocodes?.forEach((pictocode) => {
        const { code } = pictocode;
        if (pictocodeMap.has(code)) {
          pictocodeMap.get(code).counter += 1;
        } else {
          const element = {
            icon: pictocode?.class,
            value: pictocode?.code,
            key: pictocode?.i18n,
          } as WeatherData;
          pictocodeMap.set(code, { weatherData: element, counter: 1 });
        }
      });

      // Select most frequent pictocode to represent the day
      const pictocodeList = [...pictocodeMap.values()].sort((a, b) => a.counter - b.counter);

      // Store daily values
      aggregatedData.timestamp.push(time[0]);
      aggregatedData.precipitation.push(Math.round(sumPrecipitation * 100) / 100);
      aggregatedData.temperatureMax.push(temperatureMax);
      aggregatedData.temperatureMin.push(temperatureMin);
      aggregatedData.windSpeed.push(Math.round(avgWindSpeed * 100) / 100);
      aggregatedData.pictocode.push(pictocodeList[0].weatherData);

      currentIndex += interval;
    }

    // Build final daily data array
    return aggregatedData.timestamp
      .map((v) => v)
      .map((value: any, index: number) => {
        return {
          timestamp: value,
          temperatureMax: aggregatedData.temperatureMax[index],
          temperatureMin: aggregatedData.temperatureMin[index],
          windSpeed: aggregatedData.windSpeed[index],
          precipitation: aggregatedData.precipitation[index],
          details: [],
          weather: aggregatedData.pictocode[index],
        } as DailyWeatherData;
      });
  }
}

export interface WeatherData {
  value: number;
  key: string;
  icon: string;
}
